import React from 'react'
import Head from 'next/head'
import axios from 'axios'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import ConcertProductionPage from '../components/design-system/marketing/ConcertProductionPage'
import { concertProductionFallback } from '../content/seo/concertProduction'
import { compactSeoPublicPayload } from '../utilities/seoPublicPayload'

const getConcertSeoData = async ({ preview, previewData }) => {
  try {
    const response = await axios.get(`${SERVER_IP}/seo/pages/${preview ? 'preview' : 'published'}`, {
      params: preview
        ? {
            path: concertProductionFallback.page.path,
            token: previewData?.seoPreviewToken
          }
        : { path: concertProductionFallback.page.path }
    })

    return compactSeoPublicPayload(response.data.data, {
      source: preview ? 'draft-preview' : 'published',
      customKeys: ['concertProduction']
    })
  } catch (error) {
    console.warn(`Using source fallback for ${concertProductionFallback.page.path}: ${error.message}`)
    return compactSeoPublicPayload(concertProductionFallback, {
      source: 'source-fallback',
      customKeys: ['concertProduction']
    })
  }
}

export const getStaticProps = wrapper.getStaticProps(
  async ({ preview, previewData }) => {
    const [reviewResponse, seoData] = await Promise.all([
      axios.get(`${SERVER_IP}/google-review/public`, {
        params: {
          page: concertProductionFallback.page.path,
          limit: 3
        }
      }).catch(error => {
        console.warn(`Using review fallback for ${concertProductionFallback.page.path}: ${error.message}`)
        return { data: null }
      }),
      getConcertSeoData({ preview, previewData })
    ])

    return {
      props: {
        reviewData: reviewResponse.data,
        seoData,
        isPreview: Boolean(preview)
      },
      revalidate: 3600
    }
  }
)

const mergeConcertContent = stored => {
  const fallback = concertProductionFallback.revision.content
  const next = stored?.custom?.concertProduction || {}
  const mergeObject = key => ({ ...fallback[key], ...(next[key] || {}) })
  const hero = mergeObject('hero')
  const process = mergeObject('process')
  const logistics = mergeObject('logistics')

  // Preserve future CMS image changes while upgrading the legacy hero asset.
  if (hero.image === '/Pictures/concert-landing.jpg') {
    hero.image = fallback.hero.image
    hero.imageAlt = fallback.hero.imageAlt
  }

  // Preserve future CMS image changes while upgrading the legacy process asset.
  if (process.image === '/Pictures/stage-techs.jpg') {
    process.image = fallback.process.image
    process.imageAlt = fallback.process.imageAlt
  }

  // Preserve future CMS image changes while upgrading the legacy logistics asset.
  if (logistics.image === '/Pictures/outdoor-stage.jpg') {
    logistics.image = fallback.logistics.image
    logistics.imageAlt = fallback.logistics.imageAlt
  }

  return {
    ...fallback,
    ...next,
    hero,
    proof: next.proof?.length ? next.proof : fallback.proof,
    eventTypes: {
      ...mergeObject('eventTypes'),
      items: next.eventTypes?.items?.length ? next.eventTypes.items : fallback.eventTypes.items
    },
    capabilities: {
      ...mergeObject('capabilities'),
      items: next.capabilities?.items?.length ? next.capabilities.items : fallback.capabilities.items
    },
    caseStudies: next.caseStudies?.length ? next.caseStudies : fallback.caseStudies,
    process: {
      ...process,
      steps: next.process?.steps?.length ? next.process.steps : fallback.process.steps
    },
    logistics: {
      ...logistics,
      items: next.logistics?.items?.length ? next.logistics.items : fallback.logistics.items
    },
    faqs: next.faqs?.length ? next.faqs : fallback.faqs,
    proposal: mergeObject('proposal'),
    builder: mergeObject('builder'),
    related: {
      ...mergeObject('related'),
      items: next.related?.items?.length ? next.related.items : fallback.related.items
    }
  }
}

const ConcertProduction = ({ reviewData, seoData }) => {
  const page = seoData?.page || concertProductionFallback.page
  const revision = seoData?.revision || concertProductionFallback.revision
  const hasConcertComponentContent = Boolean(revision.content?.custom?.concertProduction)
  const seo = {
    ...concertProductionFallback.revision.seo,
    ...(hasConcertComponentContent ? (revision.seo || {}) : {})
  }
  const content = mergeConcertContent(revision.content)
  const canonical = seo.canonicalUrl || page.canonicalUrl
  const openGraph = {
    type: 'website',
    url: seo.openGraph?.url || canonical,
    title: seo.openGraph?.title || seo.title,
    description: seo.openGraph?.description || seo.description,
    images: seo.openGraph?.images || concertProductionFallback.revision.seo.openGraph.images,
    site_name: 'Miami Sound Rental'
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${canonical}#service`,
    name: 'Concert and Live Show Production',
    serviceType: 'Concert, festival, and live show technical production',
    url: canonical,
    description: seo.description,
    provider: {
      '@id': 'https://miamisoundrental.com/'
    },
    areaServed: [
      { '@type': 'City', name: 'Miami' },
      { '@type': 'City', name: 'Fort Lauderdale' },
      { '@type': 'AdministrativeArea', name: 'South Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Concert production capabilities',
      itemListElement: content.capabilities.items.map(item => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: item.title,
          description: item.description
        }
      }))
    }
  }

  return (
    <>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical={canonical}
        noindex={seo.robots?.index === false}
        nofollow={seo.robots?.follow === false}
        additionalMetaTags={[{
          name: 'keywords',
          content: [seo.primaryKeyword, ...(seo.secondaryKeywords || [])].filter(Boolean).join(', ')
        }]}
        twitter={{
          handle: '@miamisoundrent1',
          site: '@miamisoundrent1',
          cardType: seo.twitter?.cardType || 'summary_large_image'
        }}
        openGraph={openGraph}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://miamisoundrental.com/' },
          { position: 2, name: 'Concert Production', item: canonical }
        ]}
      />
      <Head>
        <script
          key='concert-production-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>
      <ConcertProductionPage content={content} reviewData={reviewData} />
    </>
  )
}

export default ConcertProduction
